// Rescue chamber add/edit page.

import React, {useEffect, useState} from 'react'
import {useNavigate, useParams} from 'react-router-dom'
import {ChipSelector} from '../../components/common/ChipSelector'
import {FormField} from '../../components/common/FormField'
import {SelectField} from '../../components/common/SelectField'
import {Header} from '../../components/Header'
import {PageShell} from '../../components/PageShell'
import {showToast} from '../../components/common/Toast'
import {supabase, DEFAULT_MINE_ID} from '../../app/supabaseClient'
import {
  CHAMBER_MAINTENANCE_STATUSES,
  CHAMBER_STATUSES,
  MINE_LEVELS,
  generateId,
} from '../../lib/constants'
import type {ChamberDetail, ChamberStatus} from './types'

type MaintenanceStatus = (typeof CHAMBER_MAINTENANCE_STATUSES)[number]

interface APOption {
  id: string
  name: string
  x: number | null
  y: number | null
  z: number | null
}

const LEVEL_OPTIONS = MINE_LEVELS.map(l => {
  // 'Level_1232' → { label: 'Level 1232', value: '1232' }
  const num = l.replace('Level_', '')
  return {label: `Level ${num}`, value: num}
})

interface FormErrors {
  name?: string
  capacity?: string
  current_occupancy?: string
  x?: string
  y?: string
  z?: string
}

function parseNumeric(s: string): number | null {
  const t = s.trim()
  if (!t) return null
  const n = Number(t)
  return Number.isFinite(n) ? n : null
}

export const ChamberFormScreen: React.FC = () => {
  const {id} = useParams<{id?: string}>()
  const navigate = useNavigate()
  const isEdit = Boolean(id)

  const [loading, setLoading] = useState(isEdit)
  const [busy, setBusy] = useState(false)
  const [errors, setErrors] = useState<FormErrors>({})

  const [name, setName] = useState('')
  const [location, setLocation] = useState('')
  const [locationDescription, setLocationDescription] = useState('')
  const [capacity, setCapacity] = useState('20')
  const [currentOccupancy, setCurrentOccupancy] = useState('0')
  const [status, setStatus] = useState<ChamberStatus>('available')
  const [maintenanceStatus, setMaintenanceStatus] = useState<MaintenanceStatus>('operational')
  const [accessPoint, setAccessPoint] = useState('')
  const [x, setX] = useState('')
  const [y, setY] = useState('')
  const [z, setZ] = useState('')
  const [level, setLevel] = useState('')

  // Access Points list for the dropdown. Fetched once on mount.
  const [aps, setAPs] = useState<APOption[]>([])

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      const {data} = await supabase
        .from('access_points')
        .select('id, name, x, y, z')
        .order('name')
      if (!cancelled) setAPs((data ?? []) as APOption[])
    })()
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (!isEdit || !id) return
    let cancelled = false
    ;(async () => {
      setLoading(true)
      const {data, error} = await supabase
        .from('rescue_chambers')
        .select(
          'id, name, location, location_description, capacity, current_occupancy, ' +
            'status, maintenance_status, last_inspection_date, installation_date, access_point, ' +
            'x, y, z, level',
        )
        .eq('id', id)
        .single<ChamberDetail>()
      if (cancelled) return
      if (error || !data) {
        showToast(`Load failed: ${error?.message ?? 'Not found'}`, 'error')
        navigate('/chambers')
        return
      }
      setName(data.name)
      setLocation(data.location)
      setLocationDescription(data.location_description ?? '')
      setCapacity(String(data.capacity))
      setCurrentOccupancy(String(data.current_occupancy))
      setStatus(data.status)
      // Coerce to one of the known maintenance values; default to operational
      // if the row stores a legacy free-text status.
      const ms = data.maintenance_status as MaintenanceStatus | null
      setMaintenanceStatus(
        ms && CHAMBER_MAINTENANCE_STATUSES.includes(ms) ? ms : 'operational',
      )
      setAccessPoint(data.access_point ?? '')
      setX(data.x != null ? String(data.x) : '')
      setY(data.y != null ? String(data.y) : '')
      setZ(data.z != null ? String(data.z) : '')
      setLevel(data.level != null ? String(data.level) : '')
      setLoading(false)
    })()
    return () => {
      cancelled = true
    }
  }, [id, isEdit, navigate])

  const validate = (): boolean => {
    const e: FormErrors = {}
    if (!name.trim()) e.name = 'Chamber name is required'
    const cap = parseNumeric(capacity)
    if (cap == null || cap <= 0 || !Number.isInteger(cap)) {
      e.capacity = 'Capacity must be a positive integer'
    }
    // Current occupancy is optional. If provided, validate range.
    if (currentOccupancy.trim()) {
      const occ = parseNumeric(currentOccupancy)
      if (occ == null || occ < 0 || !Number.isInteger(occ)) {
        e.current_occupancy = 'Occupancy must be a non-negative integer'
      } else if (cap != null && occ > cap) {
        e.current_occupancy = `Cannot exceed capacity (${cap})`
      }
    }
    if (!isEdit) {
      if (parseNumeric(x) == null) e.x = 'X required'
      if (parseNumeric(y) == null) e.y = 'Y required'
      if (parseNumeric(z) == null) e.z = 'Z required'
    }
    setErrors(e)
    return Object.keys(e).length === 0
  }

  const handleSave = async () => {
    if (!validate()) return
    setBusy(true)
    const now = new Date().toISOString()
    const cap = parseNumeric(capacity)!
    // Occupancy is optional now — default to 0 when blank.
    const occ = parseNumeric(currentOccupancy) ?? 0
    const xn = parseNumeric(x)
    const yn = parseNumeric(y)
    const zn = parseNumeric(z)
    const lvl = parseNumeric(level)
    // Location is no longer a user-required field. If empty, derive it from
    // the selected level (e.g. level=1500 → location="Level_1500"). Fallback
    // to "Unspecified" if no level either, so the NOT NULL column is satisfied.
    const locationValue =
      location.trim() ||
      (level.trim() ? `Level_${level.trim()}` : 'Unspecified')
    const payload = {
      name: name.trim(),
      location: locationValue,
      location_description: locationDescription.trim() || null,
      capacity: cap,
      current_occupancy: occ,
      status,
      maintenance_status: maintenanceStatus,
      access_point: accessPoint || null,
      x: xn,
      y: yn,
      z: zn,
      level: lvl,
      updated_at: now,
    }
    if (!isEdit) {
      const newId = generateId()
      const {error} = await supabase.from('rescue_chambers').insert({
        id: newId,
        mine_id: DEFAULT_MINE_ID,
        ...payload,
        created_at: now,
      })
      if (error) {
        showToast(`Add failed: ${error.message}`, 'error')
        setBusy(false)
        return
      }
      showToast('Chamber added.', 'success')
    } else {
      const {error} = await supabase
        .from('rescue_chambers')
        .update(payload)
        .eq('id', id!)
      if (error) {
        showToast(`Update failed: ${error.message}`, 'error')
        setBusy(false)
        return
      }
      showToast('Chamber updated.', 'success')
    }
    setBusy(false)
    navigate('/chambers')
  }

  const handleDelete = async () => {
    if (!id) return
    if (!window.confirm(`Delete chamber ${name}? Personnel associations will be removed.`)) return
    setBusy(true)
    await supabase.from('chamber_personnel').delete().eq('chamber_id', id)
    const {error} = await supabase.from('rescue_chambers').delete().eq('id', id)
    setBusy(false)
    if (error) {
      showToast(`Delete failed: ${error.message}`, 'error')
      return
    }
    showToast('Chamber deleted.', 'success')
    navigate('/chambers')
  }

  const title = isEdit ? `Edit ${name || 'Chamber'}` : 'Add Rescue Chamber'
  const saveLabel = isEdit ? 'Save Changes' : 'Add Chamber'

  return (
    <main className="flex flex-1 flex-col overflow-hidden">
      <Header title={title} backTo="/chambers" />
      <PageShell
        footer={
          <>
            {isEdit && (
              <button
                type="button"
                onClick={handleDelete}
                disabled={busy}
                className="mr-auto rounded-md border border-statusRed px-lg py-sm text-md text-statusRed hover:bg-statusRed hover:text-white disabled:opacity-40"
              >
                Delete
              </button>
            )}
            <button
              type="button"
              onClick={() => navigate('/chambers')}
              disabled={busy}
              className="rounded-md border border-surfaceBorder px-lg py-sm text-md text-textSecondary hover:border-textPrimary hover:text-textPrimary disabled:opacity-40"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={busy}
              className="rounded-md bg-primary px-lg py-sm text-md font-bold text-black hover:bg-primaryLight disabled:opacity-40"
            >
              {busy ? 'Saving…' : saveLabel}
            </button>
          </>
        }
      >
        {loading ? (
          <div className="flex h-full items-center justify-center text-textMuted">Loading…</div>
        ) : (
          <div className="flex flex-col gap-lg">
            <FormField
              label="Chamber Name"
              value={name}
              onChange={setName}
              placeholder="e.g. RSC-06"
              error={errors.name ?? null}
            />
            <FormField
              label="Location Description"
              value={locationDescription}
              onChange={setLocationDescription}
              placeholder="e.g. East Main Tunnel, Junction B"
            />

            <div className="flex gap-lg">
              <FormField
                label="Capacity"
                value={capacity}
                onChange={setCapacity}
                inputMode="numeric"
                error={errors.capacity ?? null}
              />
              <FormField
                label="Current Occupancy (optional)"
                value={currentOccupancy}
                onChange={setCurrentOccupancy}
                inputMode="numeric"
                error={errors.current_occupancy ?? null}
              />
            </div>

            <ChipSelector label="Status" options={CHAMBER_STATUSES} value={status} onChange={setStatus} />

            <ChipSelector
              label="Maintenance Status"
              options={CHAMBER_MAINTENANCE_STATUSES}
              value={maintenanceStatus}
              onChange={setMaintenanceStatus}
            />

            {/* Access Point — dropdown; picking an AP prefills the X/Y/Z coords
                from the AP's stored position. Stores the AP name (not id) in
                the access_point TEXT column so existing detail views render
                without a join. */}
            <SelectField
              label="Access Point"
              value={accessPoint}
              onChange={v => {
                setAccessPoint(v)
                if (!v) return
                const picked = aps.find(a => a.name === v)
                if (!picked) return
                if (picked.x != null) setX(String(picked.x))
                if (picked.y != null) setY(String(picked.y))
                if (picked.z != null) setZ(String(picked.z))
              }}
              options={[
                {label: '— None —', value: ''},
                ...aps.map(a => ({label: a.name, value: a.name})),
              ]}
            />

            <div className="flex gap-lg">
              <FormField label="X" value={x} onChange={setX} placeholder="0.0" error={errors.x ?? null} />
              <FormField label="Y" value={y} onChange={setY} placeholder="0.0" error={errors.y ?? null} />
              <FormField label="Z" value={z} onChange={setZ} placeholder="0.0" error={errors.z ?? null} />
              <SelectField
                label="Level"
                value={level}
                onChange={setLevel}
                options={[{label: '— None —', value: ''}, ...LEVEL_OPTIONS]}
              />
            </div>
          </div>
        )}
      </PageShell>
    </main>
  )
}
